import React, { useEffect, useState } from "react";
import { bidApi, carsApi, dossierApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";
import { Calculator, Save, Trash2, Edit3, X, Link2 } from "lucide-react";

const EMPTY = {
  number_plate: "",
  selling_price: "",
  cost_prep: "150",
  cost_warranty: "",
  cost_transport: "",
  cost_auction: "",
  cost_vehicle: "",
  margin_pct: "10",
  purchase_price: "",
  note: "",
  link_type: "",
  link_id: "",
  link_label: "",
};

const num = (v) => (v === "" || v == null ? 0 : Number(v) || 0);
const ARCHIVE_DAYS = 5;
const eur = (n) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(
    Math.round(n || 0)
  );

// Core formula (verified against the Excel sheet):
//  costs   = prep + transport + auction + vehicle
//  profit  = margin% of selling price (default 10%)
//  BOD incl = selling price − (profit + costs) × 1.21   (21% margin VAT)
//  BOD excl = BOD incl / 1.21
export function computeBid(f) {
  const selling = num(f.selling_price);
  const costs = num(f.cost_prep) + num(f.cost_warranty) + num(f.cost_transport) + num(f.cost_auction) + num(f.cost_vehicle);
  const pct = f.margin_pct === "" || f.margin_pct == null ? 10 : Number(f.margin_pct) || 0;
  const profit = (selling * pct) / 100;
  const bodIncl = selling - (profit + costs) * 1.21;
  const bodExcl = bodIncl / 1.21;
  // Actual profit for the real purchase price (same margin-VAT logic, inverted)
  const hasPurchase = f.purchase_price !== "" && f.purchase_price != null && Number(f.purchase_price) > 0;
  // Purchase price already includes the auction cost → don't subtract it twice
  const purchaseProfit = hasPurchase ? (selling - num(f.purchase_price)) / 1.21 - (costs - num(f.cost_auction)) : null;
  const purchasePct = hasPurchase && selling ? (purchaseProfit / selling) * 100 : null;
  return { costs, profit, bodIncl, bodExcl, purchaseProfit, purchasePct };
}

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1.5">{label}</span>
    <input
      {...props}
      className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-3 py-2.5 text-sm text-white placeholder:text-white/25"
    />
  </label>
);

const selectCls =
  "w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-3 py-2.5 text-sm text-white";

export default function AdminBidCalculator() {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState([]);
  const [cars, setCars] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [view, setView] = useState("active");
  const archiveCutoff = Date.now() - ARCHIVE_DAYS * 86400000;
  const isArchived = (c) => c.created_at && new Date(c.created_at).getTime() < archiveCutoff;
  const activeList = saved.filter((c) => !isArchived(c));
  const archivedList = saved.filter(isArchived);
  const visible = view === "archive" ? archivedList : activeList;

  const load = () =>
    bidApi.list().then((list) => {
      setSaved(list);
      setSelected((sel) => new Set([...sel].filter((x) => list.some((c) => c.id === x))));
    }).catch(() => {});

  const toggle = (sid) =>
    setSelected((sel) => {
      const next = new Set(sel);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  const allSelected = visible.length > 0 && visible.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visible.map((c) => c.id)));

  const removeSelected = async () => {
    if (!selected.size || !window.confirm(`${selected.size} berekening(en) verwijderen?`)) return;
    try {
      const res = await bidApi.bulkRemove([...selected]);
      if (editingId && selected.has(editingId)) cancelEdit();
      setSelected(new Set());
      toast({ title: `${res.deleted} berekening(en) verwijderd` });
      load();
    } catch (e) {
      toast({ title: "Verwijderen mislukt", description: e?.response?.data?.detail || String(e) });
    }
  };
  useEffect(() => {
    load();
    carsApi.list({ upcoming: "all", hidden: "all" }).then(setCars).catch(() => {});
    dossierApi.listByView("all").then(setDossiers).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const r = computeBid(form);

  const linkOptions = form.link_type === "car" ? cars : form.link_type === "dossier" ? dossiers : [];
  const labelFor = (item) =>
    form.link_type === "car"
      ? item.title
      : `${item.car_title || "Dossier"}${item.customer?.name ? " · " + item.customer.name : ""}`;

  const onLinkTypeChange = (e) =>
    setForm((f) => ({ ...f, link_type: e.target.value, link_id: "", link_label: "" }));
  const onLinkIdChange = (e) => {
    const id = e.target.value;
    const item = linkOptions.find((x) => x.id === id);
    setForm((f) => ({ ...f, link_id: id, link_label: item ? labelFor(item) : "" }));
  };

  const payload = () => ({
    number_plate: form.number_plate || null,
    selling_price: num(form.selling_price),
    cost_prep: num(form.cost_prep),
    cost_warranty: num(form.cost_warranty),
    cost_transport: num(form.cost_transport),
    cost_auction: num(form.cost_auction),
    cost_vehicle: num(form.cost_vehicle),
    margin_pct: num(form.margin_pct),
    purchase_price: form.purchase_price === "" ? null : num(form.purchase_price),
    note: form.note || null,
    link_type: form.link_type || null,
    link_id: form.link_id || null,
    link_label: form.link_label || null,
  });

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await bidApi.update(editingId, payload());
        toast({ title: "Berekening bijgewerkt" });
      } else {
        await bidApi.create(payload());
        toast({ title: "Berekening opgeslagen" });
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast({ title: "Opslaan mislukt", description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  const edit = (c) => {
    setEditingId(c.id);
    setForm({
      number_plate: c.number_plate ?? "",
      selling_price: c.selling_price ?? "",
      cost_prep: c.cost_prep ?? "150",
      cost_warranty: c.cost_warranty ?? "",
      cost_transport: c.cost_transport ?? "",
      cost_auction: c.cost_auction ?? "",
      cost_vehicle: c.cost_vehicle ?? "",
      margin_pct: c.margin_pct ?? "10",
      purchase_price: c.purchase_price ?? "",
      note: c.note ?? "",
      link_type: c.link_type ?? "",
      link_id: c.link_id ?? "",
      link_label: c.link_label ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const remove = async (id) => {
    if (!window.confirm("Berekening verwijderen?")) return;
    await bidApi.remove(id);
    if (editingId === id) cancelEdit();
    load();
  };

  return (
    <div className="p-4 md:p-8 max-w-[1000px]" data-testid="admin-bid-calculator">
      <Toaster />
      <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2 mb-6" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
        <Calculator className="w-6 h-6 text-[#c5a76f]" /> Bod calculator
      </h1>

      <div className="bg-[#242424] border border-white/5 rounded-lg p-5 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Nummerplaat" value={form.number_plate} onChange={set("number_plate")} placeholder="1-ABC-123" data-testid="bid-plate" />
          <Field label="Verkoopprijs (€)" type="number" inputMode="decimal" value={form.selling_price} onChange={set("selling_price")} placeholder="0" data-testid="bid-selling" />
          <Field label="Gewenste marge (%)" type="number" inputMode="decimal" value={form.margin_pct} onChange={set("margin_pct")} placeholder="10" data-testid="bid-margin" />
          <Field label="Kost prep (€)" type="number" inputMode="decimal" value={form.cost_prep} onChange={set("cost_prep")} placeholder="150" data-testid="bid-prep" />
          <Field label="Kost garantie (€)" type="number" inputMode="decimal" value={form.cost_warranty} onChange={set("cost_warranty")} placeholder="0" data-testid="bid-warranty" />
          <Field label="Kost transport (€)" type="number" inputMode="decimal" value={form.cost_transport} onChange={set("cost_transport")} placeholder="0" data-testid="bid-transport" />
          <Field label="Kost veiling (€)" type="number" inputMode="decimal" value={form.cost_auction} onChange={set("cost_auction")} placeholder="0" data-testid="bid-auction" />
          <Field label="Kosten voertuig (€)" type="number" inputMode="decimal" value={form.cost_vehicle} onChange={set("cost_vehicle")} placeholder="0" data-testid="bid-vehicle" />
        </div>

        {/* Results */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <Result label="Totale kosten" value={eur(r.costs)} testid="bid-out-costs" />
          <Result label={`Netto winst (${num(form.margin_pct)}%)`} value={eur(r.profit)} testid="bid-out-profit" />
          <Result label="BOD excl. btw" value={eur(r.bodExcl)} accent testid="bid-out-excl" />
          <Result label="BOD incl. btw" value={eur(r.bodIncl)} accent testid="bid-out-incl" />
        </div>

        {/* Actual purchase price → remaining profit */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5 pt-5 border-t border-white/5">
          <Field label="Aankoopprijs incl. veilingkost (€)" type="number" inputMode="decimal" value={form.purchase_price} onChange={set("purchase_price")} placeholder="Incl. veilingkost" data-testid="bid-purchase" />
          <PurchaseResult r={r} testid="bid-out-purchase-profit" />
          <Result
            label="Verschil t.o.v. bod incl."
            value={r.purchaseProfit == null ? "—" : eur(r.bodIncl - (num(form.purchase_price) - num(form.cost_auction)))}
            testid="bid-out-purchase-diff"
          />
        </div>

        {/* Optional link to a car / dossier */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <label className="block">
            <span className="block text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1.5 flex items-center gap-1.5">
              <Link2 className="w-3 h-3" /> Koppelen aan
            </span>
            <select value={form.link_type} onChange={onLinkTypeChange} className={selectCls} data-testid="bid-link-type">
              <option value="">Geen koppeling</option>
              <option value="car">Wagen</option>
              <option value="dossier">Dossier</option>
            </select>
          </label>
          {form.link_type && (
            <label className="block">
              <span className="block text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1.5">
                {form.link_type === "car" ? "Selecteer wagen" : "Selecteer dossier"}
              </span>
              <select value={form.link_id} onChange={onLinkIdChange} className={selectCls} data-testid="bid-link-id">
                <option value="">— kies —</option>
                {linkOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {labelFor(item)}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <label className="block mt-4">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1.5">Notitie</span>
          <textarea
            value={form.note}
            onChange={set("note")}
            rows={2}
            data-testid="bid-note"
            className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-3 py-2.5 text-sm text-white"
          />
        </label>

        <div className="flex items-center gap-3 mt-5">
          <button
            onClick={save}
            disabled={saving}
            data-testid="bid-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#c5a76f] text-black text-sm font-semibold tracking-wider uppercase rounded hover:bg-[#d8bd86] disabled:opacity-50 transition-colors"
          >
            <Save className="w-4 h-4" /> {editingId ? "Bijwerken" : "Opslaan"}
          </button>
          {editingId && (
            <button onClick={cancelEdit} data-testid="bid-cancel" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm">
              <X className="w-4 h-4" /> Annuleren
            </button>
          )}
        </div>
      </div>

      {/* Saved */}
      <div className="mt-8">
        <div className="flex items-center justify-between gap-3 mb-3 min-h-[36px]">
          <div className="flex items-center gap-1 bg-[#1a1a1a] border border-white/10 rounded p-1" data-testid="bid-view-tabs">
            {[["active", `Actief (${activeList.length})`], ["archive", `Archief (${archivedList.length})`]].map(([k, label]) => (
              <button
                key={k}
                onClick={() => { setView(k); setSelected(new Set()); }}
                data-testid={`bid-tab-${k}`}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded transition-colors ${view === k ? "bg-[#c5a76f] text-black" : "text-white/50 hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>
          {selected.size > 0 && (
            <button
              onClick={removeSelected}
              data-testid="bid-bulk-delete"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Verwijder geselecteerde ({selected.size})
            </button>
          )}
        </div>
        {visible.length === 0 ? (
          <div className="text-white/40 text-sm">
            {view === "archive" ? "Nog geen gearchiveerde berekeningen. Berekeningen gaan automatisch naar het archief na 5 dagen." : "Geen actieve berekeningen (ouder dan 5 dagen staan in het archief)."}
          </div>
        ) : (
          <div className="overflow-x-auto bg-[#242424] border border-white/5 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/40 text-[11px] uppercase tracking-wider border-b border-white/5">
                  <th className="pl-4 py-3 w-8">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="bid-select-all" className="accent-[#c5a76f] w-4 h-4 cursor-pointer" />
                  </th>
                  <th className="text-left px-4 py-3">Plaat / koppeling</th>
                  <th className="text-right px-3 py-3">Datum</th>
                  <th className="text-right px-3 py-3">Verkoopprijs</th>
                  <th className="text-right px-3 py-3">Kosten</th>
                  <th className="text-right px-3 py-3">BOD excl</th>
                  <th className="text-right px-3 py-3">BOD incl</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const cr = computeBid(c);
                  return (
                    <tr key={c.id} data-testid={`bid-row-${c.id}`} className={`border-b border-white/5 last:border-0 hover:bg-white/5 ${selected.has(c.id) ? "bg-[#c5a76f]/5" : ""}`}>
                      <td className="pl-4 py-3">
                        <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} data-testid={`bid-select-${c.id}`} className="accent-[#c5a76f] w-4 h-4 cursor-pointer" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{c.number_plate || "—"}</div>
                        {c.link_label && (
                          <div className="text-[11px] text-[#c5a76f] flex items-center gap-1 mt-0.5">
                            <Link2 className="w-3 h-3" /> {c.link_label}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right text-white/50 whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString("nl-BE") : "—"}</td>
                      <td className="px-3 py-3 text-right text-white/80">{eur(c.selling_price)}</td>
                      <td className="px-3 py-3 text-right text-white/60">{eur(cr.costs)}</td>
                      <td className="px-3 py-3 text-right text-[#c5a76f] font-semibold">{eur(cr.bodExcl)}</td>
                      <td className="px-3 py-3 text-right text-[#c5a76f] font-semibold">{eur(cr.bodIncl)}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <button onClick={() => edit(c)} data-testid={`bid-edit-${c.id}`} className="p-2 text-white/60 hover:text-[#c5a76f]">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(c.id)} data-testid={`bid-delete-${c.id}`} className="p-2 text-white/60 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Result({ label, value, accent = false, testid }) {
  return (
    <div className={`rounded-lg px-4 py-3 border ${accent ? "bg-[#c5a76f]/10 border-[#c5a76f]/40" : "bg-[#1a1a1a] border-white/5"}`} data-testid={testid}>
      <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">{label}</div>
      <div className={`text-lg font-bold ${accent ? "text-[#c5a76f]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function PurchaseResult({ r, testid }) {
  const none = r.purchaseProfit == null;
  const pos = !none && r.purchaseProfit >= 0;
  const cls = none ? "bg-[#1a1a1a] border-white/5" : pos ? "bg-emerald-500/10 border-emerald-500/40" : "bg-red-500/10 border-red-500/40";
  return (
    <div className={`rounded-lg px-4 py-3 border ${cls}`} data-testid={testid}>
      <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">
        Resterende winst{none ? "" : ` (${r.purchasePct.toFixed(1)}%)`}
      </div>
      <div className={`text-lg font-bold ${none ? "text-white/40" : pos ? "text-emerald-400" : "text-red-400"}`}>
        {none ? "Vul aankoopprijs in" : eur(r.purchaseProfit)}
      </div>
    </div>
  );
}
